/*
  The one matchMedia hook in the app.

  There were two. `DataTable/useIsStacked` (task 6a) answered "is this narrower than
  Tailwind's md?" for stacked mode, and `src/hooks/index.ts` wrapped MUI's
  `useMediaQuery` for `Grade`'s ring scale — the last MUI import outside a component.
  Task 6a deliberately left the second one alone rather than pre-empt its consumer
  change, so this task collapses them: the machinery lives here once, `useIsStacked`
  is a named query on top of it, and `src/hooks/` is gone.

  Two behaviours are load-bearing and both are carried over from `useIsStacked`:

  - **The initial value is read synchronously in the useState initialiser**, so a layout
    that depends on the query is never painted at the wrong size first.
  - **The legacy `addListener` pair is handled**, for the same reason `src/theme/theme.ts`
    handles it: Safari before 14 is in the production browserslist and has nothing else.

  No usable `matchMedia` resolves to "does not match", which is what MUI's hook did too,
  so the fallback layout is unchanged from before the migration.
*/

import { useEffect, useState } from "react";

type LegacyMediaQueryList = MediaQueryList & {
  addListener?: (listener: (event: MediaQueryListEvent) => void) => void;
  removeListener?: (listener: (event: MediaQueryListEvent) => void) => void;
};

const noop = () => {};

export const matchesMediaQuery = (query: string): boolean => {
  try {
    return !!window.matchMedia && window.matchMedia(query).matches;
  } catch (e) {
    return false;
  }
};

export const watchMediaQuery = (
  query: string,
  onChange: (matches: boolean) => void
) => {
  let list: LegacyMediaQueryList;
  try {
    if (!window.matchMedia) {
      return noop;
    }
    list = window.matchMedia(query) as LegacyMediaQueryList;
  } catch (e) {
    return noop;
  }

  const listener = (event: MediaQueryListEvent) => onChange(event.matches);

  if (typeof list.addEventListener === "function") {
    list.addEventListener("change", listener);
    return () => list.removeEventListener("change", listener);
  }
  if (typeof list.addListener === "function") {
    list.addListener(listener);
    return () => {
      if (list.removeListener) {
        list.removeListener(listener);
      }
    };
  }
  return noop;
};

export const useMediaQueryMatches = (query: string): boolean => {
  const [matches, setMatches] = useState(() => matchesMediaQuery(query));

  useEffect(() => {
    /* Re-read on mount: the viewport can have changed between render and effect. */
    setMatches(matchesMediaQuery(query));
    return watchMediaQuery(query, setMatches);
  }, [query]);

  return matches;
};

/* The shape `hooks/useIsMinWidth` had, so its one consumer reads the same. */
export const useIsMinWidth = (width: number = 0): boolean =>
  useMediaQueryMatches(`(min-width: ${width}px)`);
